import { useCallback, useEffect, useState } from 'react'
import { api, ApiError } from '../api'
import {
  Badge,
  Empty,
  Field,
  FormGrid,
  Modal,
  Notice,
  Page,
  Panel,
  StatTile,
  Table,
  TileRow,
  fmtDateTime,
  fmtNumber,
  fmtPhone,
  usePoll,
} from '../components/ui'
import type { CampaignSummary, RvmJob, RvmStats, VoicemailMessage } from '../types'

type Notice_ = { kind: 'error' | 'info' | 'success' | 'warn'; text: string } | null

/**
 * Recorded messages, and the ringless deposit queue that sends them.
 *
 * The page is built around one fact the operator must never have to infer:
 * whether this appliance is actually sending. An unconfigured install runs the
 * dry-run provider, which produces a full job trail, a compliance decision and
 * a "delivered" row for every deposit — and sends nothing at all. That is the
 * right default and a genuinely useful mode, but it looks identical to a live
 * campaign from every other screen, so it is stated at the top of this one.
 */
export default function Voicemail() {
  const [notice, setNotice] = useState<Notice_>(null)
  const [messages, setMessages] = useState<VoicemailMessage[]>([])
  const [campaigns, setCampaigns] = useState<CampaignSummary[]>([])
  const [uploading, setUploading] = useState(false)
  const [enqueueFor, setEnqueueFor] = useState<CampaignSummary | null>(null)
  const [statusFilter, setStatusFilter] = useState('')

  const stats = usePoll<RvmStats>(() => api.rvmStats(), 5000, [])
  const jobs = usePoll<{ jobs: RvmJob[] }>(
    () => api.rvmJobs({ status: statusFilter || undefined, limit: 200 }),
    5000,
    [statusFilter],
  )

  const reload = useCallback(async () => {
    try {
      const [m, c] = await Promise.all([api.voicemailMessages(), api.campaigns()])
      setMessages(m)
      setCampaigns(c)
    } catch (e) {
      setNotice({ kind: 'error', text: (e as Error).message })
    }
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  const s = stats.data

  return (
    <Page
      title="Voicemail"
      subtitle="Recorded messages, answering-machine drops, and ringless deposits"
      actions={
        <button className="primary" onClick={() => setUploading(true)}>
          Upload message
        </button>
      }
    >
      {notice && (
        <Notice kind={notice.kind} onDismiss={() => setNotice(null)}>
          {notice.text}
        </Notice>
      )}

      {s?.is_dry_run && (
        <Notice kind="warn">
          <strong>Dry run.</strong> Deposits are being gate-checked, queued and recorded, and
          nothing is being sent. Set a provider and credentials in Settings to send for real.
        </Notice>
      )}
      {s && s.enabled && !s.is_dry_run && !s.audio_base_url_set && (
        <Notice kind="error">
          No <code>rvm.audio_base_url</code> is configured. Providers fetch the recording over
          HTTP, so every deposit will fail until this is set.
        </Notice>
      )}

      {s && (
        <TileRow>
          <StatTile
            label="Channel"
            value={s.enabled ? 'Enabled' : 'Off'}
            tone={s.enabled ? 'ok' : undefined}
            sub={s.is_dry_run ? 'dry run — sending nothing' : `via ${s.provider}`}
          />
          <StatTile
            label="Sent today"
            value={fmtNumber(s.sent_today)}
            sub={s.daily_cap > 0 ? `cap ${fmtNumber(s.daily_cap)}` : 'no daily cap'}
          />
          <StatTile label="Queued" value={fmtNumber(s.by_status.queued ?? 0)} />
          <StatTile
            label="Blocked by the gate"
            value={fmtNumber(s.by_status.blocked ?? 0)}
            tone={(s.by_status.blocked ?? 0) > 0 ? 'warn' : undefined}
            sub="refused at send time"
          />
          <StatTile
            label="Failed"
            value={fmtNumber(s.by_status.failed ?? 0)}
            tone={(s.by_status.failed ?? 0) > 0 ? 'danger' : undefined}
          />
        </TileRow>
      )}

      <Panel title="Messages">
        {messages.length === 0 ? (
          <Empty
            message="No recorded messages yet"
            hint="Upload a WAV. A message must name the caller and give a number that reaches them before it can be attached to a campaign."
          />
        ) : (
          <Table
            head={['Name', 'Length', 'Identifies caller', 'Callback', 'Hash', 'Added', '']}
          >
            {messages.map((m) => (
              <tr key={m.id} className={m.active ? undefined : 'row-muted'}>
                <td>
                  <strong>{m.name}</strong>
                  {!m.active && <> <Badge tone="muted">retired</Badge></>}
                  {m.transcript && <div className="muted small">{m.transcript}</div>}
                </td>
                <td>{m.duration_ms ? `${(m.duration_ms / 1000).toFixed(1)}s` : '—'}</td>
                <td>
                  {m.identifies_caller ? (
                    <Badge tone="ok">confirmed</Badge>
                  ) : (
                    <Badge tone="warn">not confirmed</Badge>
                  )}
                </td>
                <td>{m.callback_number ? fmtPhone(m.callback_number) : '—'}</td>
                {/* The hash is what every job and call row records, so it is
                    shown rather than hidden: it is the answer to "which file
                    did this person actually hear". */}
                <td className="mono small" title={m.sha256}>
                  {m.sha256.slice(0, 12)}
                </td>
                <td>{fmtDateTime(m.created_at)}</td>
                <td className="row-actions">
                  {!m.identifies_caller && m.active && (
                    <button
                      className="ghost"
                      onClick={async () => {
                        try {
                          await api.updateVoicemail(m.id, { identifies_caller: true })
                          setNotice({
                            kind: 'success',
                            text: `"${m.name}" confirmed as identifying the caller.`,
                          })
                          void reload()
                        } catch (e) {
                          setNotice({ kind: 'error', text: (e as Error).message })
                        }
                      }}
                    >
                      Confirm
                    </button>
                  )}
                  {m.active && (
                    <button
                      className="ghost danger"
                      onClick={async () => {
                        try {
                          await api.retireVoicemail(m.id)
                          void reload()
                        } catch (e) {
                          setNotice({ kind: 'error', text: (e as Error).message })
                        }
                      }}
                    >
                      Retire
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Panel>

      <Panel title="Campaigns">
        {campaigns.length === 0 ? (
          <Empty message="No campaigns" />
        ) : (
          <Table head={['Campaign', 'Dial mode', 'Active', '']}>
            {campaigns.map((c) => (
              <tr key={c.id}>
                <td>
                  <strong>{c.name}</strong>
                </td>
                <td>{c.dial_mode}</td>
                <td>
                  {c.active ? <Badge tone="live">running</Badge> : <Badge tone="muted">off</Badge>}
                </td>
                <td className="row-actions">
                  <button className="ghost" onClick={() => setEnqueueFor(c)}>
                    Queue deposits
                  </button>
                  <button
                    className="ghost danger"
                    onClick={async () => {
                      try {
                        const r = await api.rvmCancelCampaign(c.id)
                        setNotice({
                          kind: 'info',
                          text: `Cancelled ${r.cancelled} queued deposit(s) on ${c.name}.`,
                        })
                      } catch (e) {
                        setNotice({ kind: 'error', text: (e as Error).message })
                      }
                    }}
                  >
                    Stop
                  </button>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Panel>

      {s && Object.keys(s.blocked_reasons).length > 0 && (
        <Panel title="Why deposits were refused">
          <Table head={['Reason', 'Count']}>
            {Object.entries(s.blocked_reasons)
              .sort((a, b) => b[1] - a[1])
              .map(([reason, n]) => (
                <tr key={reason}>
                  <td className="mono">{reason}</td>
                  <td>{fmtNumber(n)}</td>
                </tr>
              ))}
          </Table>
        </Panel>
      )}

      <Panel
        title="Deposit queue"
        actions={
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            {['queued', 'sending', 'delivered', 'failed', 'blocked', 'cancelled'].map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
        }
      >
        {!jobs.data || jobs.data.jobs.length === 0 ? (
          <Empty message="Nothing in the queue" />
        ) : (
          <Table head={['Number', 'Status', 'Why', 'Provider', 'Attempts', 'Queued', 'Sent', '']}>
            {jobs.data.jobs.map((j) => (
              <tr key={j.id}>
                <td className="mono">{fmtPhone(j.phone_e164)}</td>
                <td>
                  <Badge tone={statusTone(j.status)}>{j.status}</Badge>
                </td>
                <td className="mono small">{j.reason_code ?? j.error ?? '—'}</td>
                <td>{j.provider ?? '—'}</td>
                <td>{j.attempts}</td>
                <td>{fmtDateTime(j.queued_at)}</td>
                <td>{fmtDateTime(j.sent_at)}</td>
                <td className="row-actions">
                  {j.status === 'queued' && (
                    <button
                      className="ghost danger"
                      onClick={async () => {
                        try {
                          await api.rvmCancelJob(j.id)
                        } catch (e) {
                          setNotice({ kind: 'error', text: (e as Error).message })
                        }
                      }}
                    >
                      Cancel
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Panel>

      {uploading && (
        <UploadModal
          onClose={() => setUploading(false)}
          onDone={(msg) => {
            setUploading(false)
            setNotice({ kind: 'success', text: `Uploaded "${msg.name}".` })
            void reload()
          }}
          onError={(text) => setNotice({ kind: 'error', text })}
        />
      )}

      {enqueueFor && (
        <EnqueueModal
          campaign={enqueueFor}
          messages={messages.filter((m) => m.active && m.identifies_caller)}
          onClose={() => setEnqueueFor(null)}
          onDone={(queued) => {
            setEnqueueFor(null)
            setNotice({
              kind: 'success',
              text: `${fmtNumber(queued)} deposit(s) queued. Each one is compliance-checked at the moment it is sent, not now.`,
            })
          }}
          onError={(text) => setNotice({ kind: 'error', text })}
        />
      )}
    </Page>
  )
}

function statusTone(s: string): 'ok' | 'warn' | 'danger' | 'live' | 'muted' | undefined {
  switch (s) {
    case 'delivered':
      return 'ok'
    case 'sending':
      return 'live'
    case 'blocked':
      return 'warn'
    case 'failed':
      return 'danger'
    case 'cancelled':
      return 'muted'
    default:
      return undefined
  }
}

function UploadModal({
  onClose,
  onDone,
  onError,
}: {
  onClose: () => void
  onDone: (m: VoicemailMessage) => void
  onError: (text: string) => void
}) {
  const [file, setFile] = useState<File | null>(null)
  const [name, setName] = useState('')
  const [transcript, setTranscript] = useState('')
  const [callback, setCallback] = useState('')
  const [identifies, setIdentifies] = useState(false)
  const [busy, setBusy] = useState(false)

  const submit = async () => {
    if (!file) return onError('Choose an audio file.')
    setBusy(true)
    try {
      const fd = new FormData()
      fd.append('file', file)
      fd.append('name', name || file.name)
      if (transcript) fd.append('transcript', transcript)
      if (callback) fd.append('callback_number', callback)
      fd.append('identifies_caller', String(identifies))
      onDone(await api.uploadVoicemail(fd))
    } catch (e) {
      onError(e instanceof ApiError ? e.message : (e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal title="Upload a voicemail message" onClose={onClose}>
      <FormGrid>
        <Field label="Audio file" hint="WAV. Asterisk plays this and providers fetch it.">
          <input
            type="file"
            accept="audio/*,.wav,.mp3"
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null
              setFile(f)
              if (f && !name) setName(f.name.replace(/\.[^.]+$/, ''))
            }}
          />
        </Field>
        <Field label="Name">
          <input value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field
          label="Callback number"
          hint="The number a recipient calling back should reach."
        >
          <input value={callback} onChange={(e) => setCallback(e.target.value)} />
        </Field>
        <Field
          label="Script"
          hint="What the recording says, as approved. The file's hash proves which audio went out; this records what it was meant to say."
        >
          <textarea rows={4} value={transcript} onChange={(e) => setTranscript(e.target.value)} />
        </Field>
      </FormGrid>

      {/* Not a formality. A prerecorded message has to name the caller and give
          a number that reaches them, and that is a property of the recording
          rather than of the dialer -- so the only useful thing the software can
          do is decline to send one nobody has listened to and confirmed. */}
      <label className="check">
        <input
          type="checkbox"
          checked={identifies}
          onChange={(e) => setIdentifies(e.target.checked)}
        />
        <span>
          I have listened to this recording and it names the caller and gives a callback number.
        </span>
      </label>
      <p className="muted small">
        A message cannot be attached to a campaign until this is confirmed.
      </p>

      <div className="modal-actions">
        <button className="ghost" onClick={onClose}>
          Cancel
        </button>
        <button className="primary" onClick={submit} disabled={busy || !file}>
          {busy ? 'Uploading…' : 'Upload'}
        </button>
      </div>
    </Modal>
  )
}

function EnqueueModal({
  campaign,
  messages,
  onClose,
  onDone,
  onError,
}: {
  campaign: CampaignSummary
  messages: VoicemailMessage[]
  onClose: () => void
  onDone: (queued: number) => void
  onError: (text: string) => void
}) {
  const [messageId, setMessageId] = useState(messages[0]?.id ?? '')
  const [limit, setLimit] = useState(500)
  const [busy, setBusy] = useState(false)

  return (
    <Modal title={`Queue deposits — ${campaign.name}`} onClose={onClose}>
      {messages.length === 0 ? (
        <Notice kind="warn">
          No message is available. Upload one and confirm that it names the caller first.
        </Notice>
      ) : (
        <FormGrid>
          <Field label="Message">
            <select value={messageId} onChange={(e) => setMessageId(e.target.value)}>
              {messages.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label="Maximum to queue"
            hint="A ceiling on this batch, not a target. Queueing does not send."
          >
            <input
              type="number"
              min={1}
              max={250000}
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
            />
          </Field>
        </FormGrid>
      )}

      <p className="muted small">
        Every deposit is put through the same compliance gate as a live call, at the moment it is
        sent rather than now — so a number suppressed between queueing and sending is still caught.
        Refused deposits appear in the queue as <code>blocked</code> with their reason.
      </p>

      <div className="modal-actions">
        <button className="ghost" onClick={onClose}>
          Cancel
        </button>
        <button
          className="primary"
          disabled={busy || !messageId}
          onClick={async () => {
            setBusy(true)
            try {
              const r = await api.rvmEnqueue(campaign.id, { message_id: messageId, limit })
              onDone(r.queued)
            } catch (e) {
              onError((e as Error).message)
            } finally {
              setBusy(false)
            }
          }}
        >
          {busy ? 'Queueing…' : 'Queue deposits'}
        </button>
      </div>
    </Modal>
  )
}
